import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import Close from 'icons/close';
import ReCaptchaUI from '../../../components/ui/recaptcha';
import { Input } from '../../../components/ui/input';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import useProduct from '../../hooks/useProduct';
import toast from 'react-hot-toast';
import useFormInteraction from 'helpers/hooks/useFormInteraction';
import FormEventsAnalyticsTracker from 'components/analaytics/form-event-anayltics-tracker';

export interface SocialShareModalProps {
  modelOpen: boolean;
  onClose: () => void;
  onShare: () => void;
  url: string;
}

const socialShareSchema = Yup.object({
  shareEmail: Yup.string().email('Email must be valid').required('Please enter a valid email address.'),
  recipientEmail: Yup.string().email('Email must be valid').required('Please enter a valid email address.'),
});

const SocialShareModal: React.FC<SocialShareModalProps> = ({ modelOpen, onShare, onClose, url }) => {
  const { shareProductLink } = useProduct();
  const [isReCaptchaVerified, setIsReCaptchaVerified] = useState(false);
  const [reCaptchaError, setReCaptchaError] = useState(false);
  const [isURLCopied, setIsURLCopied] = useState(false);
  const { handleStartInteraction, handleSubmitInteraction } = useFormInteraction('Social Share');

  const {
    handleSubmit,
    register,
    formState: { errors, isDirty },
  } = useForm({ mode: 'onBlur', resolver: yupResolver(socialShareSchema) });

  useEffect(() => {
    if (isDirty) {
      handleStartInteraction();
    }
  }, [isDirty]);

  return (
    <Transition.Root show={modelOpen} as={Fragment}>
      <Dialog data-theme="default" className="default fixed inset-0 z-50 overflow-y-auto" onClose={() => onClose}>
        <>
          <section className="flex items-end justify-center px-4 pb-20 pt-4 text-left sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 opacity-75 transition-opacity" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <section className="absolute inset-0" onClick={onClose}>
                <section
                  className="dark:bg-primary-200 absolute left-1/2 top-1/2 w-[90%] -translate-x-1/2 -translate-y-1/2 overflow-auto bg-white py-6 md:w-[500px]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <section className="flex justify-between px-4 sm:px-6 lg:px-8">
                    <h1 className="text-sol-400 mx-auto mb-9 w-max text-center text-2xl font-bold leading-5 tracking-wider after:mx-auto after:mt-4 after:block after:h-[1px] after:w-36 after:bg-[#B19A6A] after:content-['']">
                      Share via Email
                    </h1>
                    <section className="pt-2">
                      <button type="button" onClick={onClose}>
                        <Close className="fill-sol-300" />
                      </button>
                    </section>
                  </section>
                  <section className="customtablescrollbar max-h-[calc(90vh_-_80px)] overflow-y-auto px-4 sm:px-6 lg:px-8">
                    <section className="mb-3">
                      <label htmlFor="shareURL" className="text-sol-400 flex text-sm font-bold leading-[17px]">
                        Share via Link
                      </label>
                      <section className="mt-3 flex items-center border-[1px] border-[#e5e7eb]">
                        <input
                          readOnly
                          type="text"
                          id="shareURL"
                          defaultValue={url}
                          className="block w-full rounded-r-md border-0 bg-gray-50 py-2 text-left text-sm text-black focus:outline-offset-0 focus:ring-0"
                        />
                        <button
                          className={`px-6 sm:px-4 ${
                            isURLCopied
                              ? 'border-sol-500 border-l border-black bg-white'
                              : 'rounded-r-md bg-[#404040] px-2 py-2 text-white'
                          }`}
                          onClick={() => {
                            navigator.clipboard.writeText(url);
                            setIsURLCopied(true);
                          }}
                        >
                          {isURLCopied ? `Copied` : 'Copy'}
                        </button>
                      </section>
                      <p className="text-sol-400 mt-10 text-base font-bold leading-[29px]">Share via Email</p>
                    </section>

                    <form
                      onSubmit={handleSubmit(async (data) => {
                        if (isReCaptchaVerified) {
                          const response = await shareProductLink(data.recipientEmail, url);

                          if (response) {
                            toast.success('Email sent successfully');
                            handleSubmitInteraction({ email: data.recipientEmail });
                            onShare();
                          } else {
                            toast.error('Email failed to send. Please contact your email administrator');
                          }

                          onClose();
                        } else {
                          setReCaptchaError(true);
                        }
                      })}
                      className="flex w-full flex-col gap-y-6"
                    >
                      {errors && <FormEventsAnalyticsTracker formName="Social Share" errors={errors} />}
                      <div className="mt-3 flex flex-col gap-y-3">
                        <label className="text-sm font-semibold leading-[17px] text-primary-gray" htmlFor="loginEmail">
                          Email Address
                          <span className="ml-[1px] text-red-500">*</span>
                        </label>
                        <Input
                          aria-label="Email Address"
                          className="w-full"
                          type="email"
                          autoComplete="email"
                          required
                          {...register('shareEmail')}
                        />
                        {errors.shareEmail && (
                          <p className="text-sm font-normal leading-[17px] text-[#DA0F0F]">
                            {errors.shareEmail.message}
                          </p>
                        )}
                      </div>

                      <div className="mt-3 flex flex-col gap-y-3">
                        <label className="text-sm font-semibold leading-[17px] text-primary-gray" htmlFor="loginEmail">
                          Recipient Email Address
                          <span className="ml-[1px] text-red-500">*</span>
                        </label>
                        <Input
                          aria-label="Recipient Email Address"
                          className="w-full"
                          type="email"
                          autoComplete="email"
                          required
                          {...register('recipientEmail')}
                        />
                        {errors.recipientEmail && (
                          <p className="text-sm font-normal leading-[17px] text-[#DA0F0F]">
                            {errors.recipientEmail.message}
                          </p>
                        )}
                      </div>

                      <ReCaptchaUI
                        setIsReCaptchaVerified={setIsReCaptchaVerified}
                        setReCaptchaError={setReCaptchaError}
                      />
                      {reCaptchaError && (
                        <p className="mt-1 text-xs font-semibold text-[#DA0F0F]">Please complete the reCaptcha</p>
                      )}

                      <section className="mt-8 flex flex-col items-center gap-4 md:mb-4 md:flex-row md:gap-6">
                        <button
                          id="shareButton"
                          type="submit"
                          className={`flex w-full items-center justify-center rounded-md bg-[#404040] px-16 py-2 text-white md:w-auto ${
                            Object.keys(errors).length === 0 ? '' : 'cursor-not-allowed'
                          }`}
                        >
                          Share
                        </button>
                        <button
                          id="cancelButton"
                          type="button"
                          onClick={onClose}
                          className="btn-cancel flex w-full items-center justify-center underline underline-offset-2 md:w-auto"
                        >
                          Cancel
                        </button>
                      </section>
                    </form>
                  </section>
                </section>
              </section>
            </Transition.Child>
          </section>
        </>
      </Dialog>
    </Transition.Root>
  );
};

export default SocialShareModal;
